import { useState } from "react";
import { formatBytes } from "@/lib/format";
import { stackStorageSeries, type StorageSeriesInput } from "@/lib/storageSeries";
import type { InsightsRange } from "@/lib/api";

const CHART_W = 600;
const CHART_H = 220;
const PAD_LEFT = 58;
const PAD_RIGHT = 12;
const PAD_TOP = 12;
const PAD_BOTTOM = 36;

const INNER_W = CHART_W - PAD_LEFT - PAD_RIGHT;
const INNER_H = CHART_H - PAD_TOP - PAD_BOTTOM;

function formatXLabel(iso: string, range: InsightsRange): string {
  const d = new Date(iso);
  if (range === "24h") {
    return d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
  }
  return d.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" });
}

interface StorageStackedChartProps {
  title: string;
  /** Uma entrada por categoria, de baixo para cima na pilha. */
  series: StorageSeriesInput[];
  range: InsightsRange;
  footer?: string;
}

/**
 * Composição do armazenamento ao longo do tempo, uma faixa por categoria.
 *
 * As categorias chegam prontas do backend e são disjuntas — a pilha é a soma
 * delas, e o topo é o espaço total ocupado. Antes desta fase a mesma seção
 * eram quatro gráficos separados que mediam réguas sobrepostas: quem olhava
 * não tinha como somar nada.
 */
export function StorageStackedChart({ title, series, range, footer }: StorageStackedChartProps) {
  const [hovered, setHovered] = useState<string | null>(null);
  const { buckets, bands, max, latestTotal } = stackStorageSeries(series);

  if (buckets.length === 0 || max <= 0) {
    return (
      <div className="flex h-72 flex-col rounded-lg border border-ink-700 bg-ink-900 p-4">
        <p className="mb-2 text-xs font-medium text-ink-300">{title}</p>
        <div className="flex flex-1 items-center justify-center">
          <p className="text-sm text-ink-500">Sem dados para o período</p>
        </div>
      </div>
    );
  }

  const times = buckets.map((b) => new Date(b).getTime());
  const minT = Math.min(...times);
  const maxT = Math.max(...times);
  const rangeT = maxT - minT || 1;

  const toX = (t: number) => PAD_LEFT + ((t - minT) / rangeT) * INNER_W;
  const toY = (v: number) => PAD_TOP + INNER_H - (v / max) * INNER_H;

  const areaPath = (band: (typeof bands)[number]): string => {
    const topo = band.slices
      .map((s) => `${toX(new Date(s.bucketStart).getTime())},${toY(s.to)}`)
      .join(" L ");
    const base = [...band.slices]
      .reverse()
      .map((s) => `${toX(new Date(s.bucketStart).getTime())},${toY(s.from)}`)
      .join(" L ");
    return `M ${topo} L ${base} Z`;
  };

  const yTicks = 4;
  const yLabels = Array.from({ length: yTicks + 1 }, (_, i) => {
    const v = (max * i) / yTicks;
    return { v, y: toY(v) };
  });

  const xTickCount = Math.min(buckets.length, range === "24h" ? 6 : 7);
  const xStep = Math.max(1, Math.floor(buckets.length / xTickCount));
  const xLabels = buckets
    .filter((_, i) => i % xStep === 0 || i === buckets.length - 1)
    .map((b) => ({ label: formatXLabel(b, range), x: toX(new Date(b).getTime()) }));

  return (
    <div className="rounded-lg border border-ink-700 bg-ink-900 p-4">
      <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
        <p className="text-xs font-medium text-ink-300">{title}</p>
        <p className="text-xs tabular-nums text-ink-400">
          {formatBytes(latestTotal)} <span className="text-ink-600">no total</span>
        </p>
      </div>

      <div className="relative w-full overflow-hidden">
        <svg
          viewBox={`0 0 ${CHART_W} ${CHART_H}`}
          width="100%"
          aria-label={title}
          style={{ display: "block" }}
        >
          {yLabels.map(({ y }, i) => (
            <line
              key={i}
              x1={PAD_LEFT}
              y1={y}
              x2={CHART_W - PAD_RIGHT}
              y2={y}
              stroke="#2a2f3a"
              strokeWidth="1"
            />
          ))}

          {yLabels.map(({ v, y }, i) => (
            <text
              key={i}
              x={PAD_LEFT - 6}
              y={y}
              textAnchor="end"
              dominantBaseline="middle"
              fontSize="10"
              fill="#6b7280"
            >
              {formatBytes(v)}
            </text>
          ))}

          {xLabels.map(({ label, x }, i) => (
            <text key={i} x={x} y={CHART_H - 8} textAnchor="middle" fontSize="10" fill="#6b7280">
              {label}
            </text>
          ))}

          {bands.map((band) => (
            <path
              key={band.key}
              d={areaPath(band)}
              fill={band.color}
              stroke={band.color}
              strokeWidth="0.75"
              opacity={hovered === null || hovered === band.key ? 0.85 : 0.25}
            />
          ))}
        </svg>
      </div>

      <ul className="mt-3 flex flex-wrap gap-x-4 gap-y-1.5">
        {[...bands].reverse().map((band) => (
          <li key={band.key}>
            <button
              type="button"
              onMouseEnter={() => setHovered(band.key)}
              onMouseLeave={() => setHovered(null)}
              onFocus={() => setHovered(band.key)}
              onBlur={() => setHovered(null)}
              className="flex items-center gap-1.5 text-xs text-ink-400 transition-colors hover:text-ink-100"
              aria-label={`${band.label}: ${formatBytes(band.latest)}`}
            >
              <span
                className="h-2 w-2 shrink-0 rounded-sm"
                style={{ backgroundColor: band.color }}
                aria-hidden="true"
              />
              <span>{band.label}</span>
              <span className="tabular-nums text-ink-500">{formatBytes(band.latest)}</span>
            </button>
          </li>
        ))}
      </ul>

      {footer && <p className="mt-3 text-xs text-ink-500">{footer}</p>}
    </div>
  );
}
